import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { setGlobalTimezone } from "@/lib/formatLocalTime";

// Common timezones for the selector
export const COMMON_TIMEZONES = [
  { value: "auto", label: "Auto-detect (Device)" },
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Central European (CET)" },
  { value: "Europe/Berlin", label: "Berlin (CET)" },
  { value: "Europe/Madrid", label: "Madrid (CET)" },
  { value: "Europe/Rome", label: "Rome (CET)" },
  { value: "Europe/Amsterdam", label: "Amsterdam (CET)" },
  { value: "Europe/Moscow", label: "Moscow (MSK)" },
  { value: "Asia/Dubai", label: "Dubai (GST)" },
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Asia/Shanghai", label: "China (CST)" },
  { value: "Australia/Sydney", label: "Sydney (AEST)" },
  { value: "Australia/Melbourne", label: "Melbourne (AEST)" },
  { value: "Pacific/Auckland", label: "New Zealand (NZST)" },
  { value: "Africa/Lagos", label: "Lagos (WAT)" },
  { value: "Africa/Johannesburg", label: "Johannesburg (SAST)" },
  { value: "America/Sao_Paulo", label: "São Paulo (BRT)" },
  { value: "America/Mexico_City", label: "Mexico City (CST)" },
];

const TIMEZONE_STORAGE_KEY = "statlign_user_timezone";
const LEGACY_TIMEZONE_STORAGE_KEY = "7odds_user_timezone";

/** Reads the stored timezone, migrating any value from the legacy brand key. */
function readStoredTimezone(): string | null {
  if (typeof window === "undefined") return null;
  const current = localStorage.getItem(TIMEZONE_STORAGE_KEY);
  if (current) return current;
  const legacy = localStorage.getItem(LEGACY_TIMEZONE_STORAGE_KEY);
  if (legacy) {
    localStorage.setItem(TIMEZONE_STORAGE_KEY, legacy);
    localStorage.removeItem(LEGACY_TIMEZONE_STORAGE_KEY);
    return legacy;
  }
  return null;
}

interface TimezoneContextType {
  timezone: string;
  timezoneAbbr: string;
  setTimezone: (tz: string) => void;
  isAutoDetect: boolean;
  deviceTimezone: string;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

function getDeviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

function getTimezoneAbbr(tz: string): string {
  try {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find((part) => part.type === "timeZoneName");
    return tzPart?.value || tz;
  } catch {
    return tz;
  }
}

export function TimezoneProvider({ children }: { children: ReactNode }) {
  const deviceTimezone = getDeviceTimezone();

  const [selectedTimezone, setSelectedTimezone] = useState<string>(() => {
    if (typeof window !== "undefined") {
      return readStoredTimezone() || "auto";
    }
    return "auto";
  });

  const isAutoDetect = selectedTimezone === "auto";
  const timezone = isAutoDetect ? deviceTimezone : selectedTimezone;
  const timezoneAbbr = getTimezoneAbbr(timezone);

  const handleSetTimezone = (tz: string) => {
    setSelectedTimezone(tz);
    localStorage.setItem(TIMEZONE_STORAGE_KEY, tz);
    setGlobalTimezone(tz === "auto" ? null : tz);
  };

  useEffect(() => {
    const stored = readStoredTimezone();
    if (stored) {
      setSelectedTimezone(stored);
      setGlobalTimezone(stored === "auto" ? null : stored);
    }
  }, []);

  useEffect(() => {
    setGlobalTimezone(isAutoDetect ? null : timezone);
  }, [timezone, isAutoDetect]);

  const value: TimezoneContextType = {
    timezone,
    timezoneAbbr,
    setTimezone: handleSetTimezone,
    isAutoDetect,
    deviceTimezone,
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
}

export function useTimezone(): TimezoneContextType {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error("useTimezone must be used within a TimezoneProvider");
  }
  return context;
}
